import { useCallback, useSyncExternalStore } from "react";
import { forcedReducedMotion } from "../lib/interaction-preferences";
import { useMediaQuery } from "./use-media-query";

/**
 * Whether motion should be minimized. JS-side signal for animation the CSS
 * engine doesn't drive (WAAPI springs, animated chart series) — CSS-driven
 * motion uses the `motion-reduce:` variant instead.
 *
 * Two sources, same precedence as the stylesheet: a forced preference on
 * `<html>` (`applyMotionPreference`) wins; with none, the OS
 * `prefers-reduced-motion` query answers.
 */
export function usePrefersReducedMotion(): boolean {
  const system = useMediaQuery("(prefers-reduced-motion: reduce)");

  const subscribe = useCallback((callback: () => void) => {
    const observer = new MutationObserver(callback);
    observer.observe(document.documentElement, {
      attributeFilter: ["data-reduce-motion"],
    });
    return () => observer.disconnect();
  }, []);

  const forced = useSyncExternalStore(
    subscribe,
    forcedReducedMotion,
    getServerSnapshot,
  );

  return forced ?? system;
}

// The server has no document; `null` defers to the media query's own server
// snapshot (false), and hydration corrects both together.
function getServerSnapshot(): boolean | null {
  return null;
}
